import { useState, useEffect, useCallback } from 'react';
import { DataSourceWizardModel } from '../models/DataSourceWizardModel';
import { CsvReviewModel } from '../models/CsvReviewModel';
import { apiService } from '../services/api';

/**
 * ViewModel for Data Source Wizard
 * Handles business logic and state management
 */
/**
 * @param model the shared DataSourceWizardModel
 * @param onRequestClose optional. PRD-1359: the wizard now lives in the URL
 *   (`/data-sources?new=1`) so the browser Back button can return to the
 *   dataset list. When it does, closing has to be a NAVIGATION — closing the
 *   model directly would leave the param behind and the App would just reopen
 *   it. The App passes its navigating close here so Escape and the backdrop go
 *   the same way as the Cancel button. Falls back to closing the model
 *   directly, which keeps this hook usable on its own.
 */
export const useDataSourceWizardViewModel = (model, onRequestClose) => {
  const [state, setState] = useState({
    isOpen: false,
    currentStep: 1,
    totalSteps: 3,
    datasetId: null,
    datasetIds: [],
    formData: {
      name: '',
      description: '',
      connectionData: null,
      fileDatasetNames: [],
      groupAsOneDataset: false,
      groupedDatasetName: '',
    },
    selectedFiles: [],
    promptValidated: false,
    validationFeedback: null,
    refinementNotes: '',
  });

  useEffect(() => {
    const unsubscribe = model.subscribe((newState) => {
      setState(newState);
    });

    // Initialize state
    setState(model.getState());

    return unsubscribe;
  }, [model]);

  const openWizard = useCallback((step = 1) => {
    model.open(step);
  }, [model]);

  // Pure: closes the model and nothing else. The URL->model sync in App needs
  // exactly this, with no navigation of its own, or it could never settle.
  const closeWizard = useCallback(() => {
    model.close();
  }, [model]);

  // What a USER-initiated dismissal goes through — Escape, the backdrop, Cancel.
  // The wizard lives at `/data-sources?new=1`, so dismissing it has to drop the
  // param; closing the model alone would leave the App's effect looking at a URL
  // that still says the wizard is open, and it would reopen immediately.
  const requestClose = useCallback(() => {
    if (onRequestClose) onRequestClose();
    else closeWizard();
  }, [onRequestClose, closeWizard]);

  const nextStep = useCallback(() => {
    model.nextStep();
  }, [model]);

  const previousStep = useCallback(() => {
    model.previousStep();
  }, [model]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      requestClose();
    }
  }, [requestClose]);

  const handleEscapeKey = useCallback((e) => {
    if (e.key === 'Escape' && state.isOpen) {
      requestClose();
    }
  }, [state.isOpen, requestClose]);

  useEffect(() => {
    if (state.isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [state.isOpen, handleEscapeKey]);

  const createDataset = useCallback(async (data) => {
    console.group('[ViewModel] createDataset');
    console.log('Input data:', data);
    console.log('Model state before:', model.getState());

    try {
      console.log('[ViewModel] Calling apiService.createDataset...');
      const response = await apiService.createDataset(data);
      console.log('[ViewModel] API response received:', response);

      if (response && response.id) {
        console.log('[ViewModel] Setting dataset ID:', response.id);
        model.setDatasetId(response.id);

        // Check if prompt is validated
        const isValidated = response.prompt_validated === true;
        console.log('[ViewModel] Prompt validated status:', isValidated);
        model.setPromptValidated(isValidated);

        // Store validation feedback if available
        if (response.prompt_validation_feedback) {
          console.log('[ViewModel] Validation feedback received:', response.prompt_validation_feedback);
          model.setValidationFeedback(response.prompt_validation_feedback);
        }

        console.log('[ViewModel] Model state after:', model.getState());
        console.log('[ViewModel] Dataset created successfully!');
      } else {
        console.warn('[ViewModel] Response missing ID:', response);
      }

      console.groupEnd();
      return response;
    } catch (error) {
      console.error('[ViewModel] Error in createDataset:', error);
      console.error('[ViewModel] Error details:', {
        message: error.message,
        status: error.status,
        responseData: error.responseData,
        stack: error.stack
      });
      console.groupEnd();
      throw error;
    }
  }, [model]);

  const uploadFiles = useCallback(async (datasetId, files, source = 'local', storageUrl = '', token = '') => {
    console.group('[ViewModel] uploadFiles');
    console.log('Dataset ID:', datasetId);
    console.log('Source:', source);
    if (source === 'local') {
      console.log('Files:', Array.from(files || []).map(f => ({ name: f.name, size: f.size, type: f.type })));
    } else if (source === 'azure') {
      console.log('Storage URL:', storageUrl);
    }

    try {
      console.log('[ViewModel] Calling apiService.uploadFiles...');
      const response = await apiService.uploadFiles(datasetId, files, source, storageUrl, token);
      console.log('[ViewModel] Files uploaded successfully:', response);
      // Store files in model after successful upload (only for local)
      if (source === 'local' && files) {
        model.setSelectedFiles(Array.from(files));
      }
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('[ViewModel] Error in uploadFiles:', error);
      console.error('[ViewModel] Error details:', {
        message: error.message,
        status: error.status,
        responseData: error.responseData
      });
      console.groupEnd();
      throw error;
    }
  }, [model]);

  const setFormData = useCallback((formData) => {
    model.setFormData(formData);
    // Reset validation status when form data changes
    if (model.promptValidated) {
      model.setPromptValidated(false);
    }
  }, [model]);

  const setSelectedFiles = useCallback((files) => {
    model.setSelectedFiles(files);
  }, [model]);

  const clearFormData = useCallback(() => {
    model.clearFormData();
  }, [model]);

  const setDatasetIds = useCallback((ids) => {
    model.setDatasetIds(ids);
  }, [model]);

  const extractFields = useCallback(async (datasetId) => {
    console.group('[ViewModel] extractFields');
    console.log('Dataset ID:', datasetId);

    try {
      // Trigger the interactive flow
      console.log('[ViewModel] Calling apiService.extractFields...');
      const response = await apiService.extractFields(datasetId);
      console.log('[ViewModel] Flow triggered, response:', response);

      // Persist job_id for HITL review lookup from DataSources page
      if (response && response.job_id) {
        model.setJobId(response.job_id);
        // Resolve dataset name: formData.name is unused since Step1 was removed.
        // Use groupedDatasetName (grouped mode) or fetch from API as fallback.
        const { formData } = model.getState();
        let datasetName = formData?.groupedDatasetName || '';
        if (!datasetName) {
          try {
            const dataset = await apiService.getDataset(datasetId);
            datasetName = dataset?.name || '';
          } catch {
            datasetName = '';
          }
        }
        CsvReviewModel.persistJobMapping(datasetId, response.job_id, datasetName);
      }

      console.log('[ViewModel] Extraction started successfully');
      console.groupEnd();

      return response;
    } catch (error) {
      console.error('[ViewModel] Error in extractFields:', error);
      console.groupEnd();
      throw error;
    }
  }, [model]);

  const validatePrompt = useCallback(async (datasetId, prompt) => {
    console.group('[ViewModel] validatePrompt');
    console.log('Dataset ID:', datasetId);
    console.log('Prompt:', prompt);

    try {
      const response = await apiService.validatePrompt(datasetId, prompt);
      console.log('[ViewModel] Validate prompt response:', response);

      // Update validation status
      const isValidated = response.status === 'valid';
      model.setPromptValidated(isValidated);

      if (response.message) {
        model.setValidationFeedback({
          status: response.status,
          message: Array.isArray(response.message) ? response.message : [response.message],
          suggestions: response.suggestions || [],
        });
      }

      return response;
    } catch (error) {
      console.error('[ViewModel] Error in validatePrompt:', error);
      throw error;
    } finally {
      console.groupEnd();
    }
  }, [model]);

  const setRefinementNotes = useCallback((notes) => {
    // Store refinement notes in state
    setState(prev => ({ ...prev, refinementNotes: notes }));
  }, []);

  const publishDataset = useCallback(async (datasetId) => {
    console.group('[ViewModel] publishDataset');
    console.log('Dataset ID:', datasetId);

    try {
      const response = await apiService.updateDatasetStatus(datasetId, 'published');
      console.log('[ViewModel] Dataset published successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('[ViewModel] Error publishing dataset:', error);
      console.groupEnd();
      throw error;
    }
  }, []);

  const saveAsDraft = useCallback(async (datasetId) => {
    console.group('[ViewModel] saveAsDraft');
    console.log('Dataset ID:', datasetId);

    try {
      const response = await apiService.updateDatasetStatus(datasetId, 'draft');
      console.log('[ViewModel] Dataset saved as draft:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('[ViewModel] Error saving as draft:', error);
      console.groupEnd();
      throw error;
    }
  }, []);

  return {
    ...state,
    openWizard,
    closeWizard,
    requestClose,
    nextStep,
    previousStep,
    handleBackdropClick,
    createDataset,
    uploadFiles,
    setFormData,
    setSelectedFiles,
    clearFormData,
    extractFields,
    validatePrompt,
    setRefinementNotes,
    publishDataset,
    saveAsDraft,
    setDatasetIds,
  };
};
